/**
 * First-party API adapter for Request Correspondence (#1069).
 *
 * THE POINT OF THIS FILE IS WHAT IT DOES NOT CONTAIN. The retired
 * `flarumProvider` (deleted in #1069 A3) held a
 * messaging-backend MASTER API KEY in the client bundle, which NFR34 forbids in
 * any deployed environment. This arm speaks to our own `/api/v1` with the
 * caller's existing Okta session, and the credential lives on the server:
 *
 *   BEFORE   client ──(master API key)──> Flarum
 *   AFTER    client ──(Okta session)──> our API ──(master key, server-side)──> Flarum
 *
 * The client no longer knows what stores the messages. No vendor name, no URL,
 * no key, and no vendor-shaped payload crosses this boundary — a cursor is an
 * opaque string minted by our API, and an error is a terse message our API
 * composed.
 *
 * ✅ SPACE MEMBERSHIP IS ENFORCED SERVER-SIDE on every read and every write
 * (`server/services/correspondence-authorization.service.ts`); the proof is
 * `server/routes/__tests__/correspondence.route.test.ts`. Do not read a green
 * contract suite as "correspondence is secure": it asserts behaviour, not
 * confidentiality.
 *
 * Since Story 1069-1.3 the write replies are the first-party shapes: a post
 * and an edit answer the saved `Message` directly, a create answers
 * `{ thread, message }` (two things were created), a delete answers 204. A
 * reply that is not the documented shape throws rather than rendering.
 *
 * Since Story 1069-1.2 every collection this provider reads is a `Page<T>`,
 * `{ items: T[] }`, exactly as `server/services/correspondence.types.ts`
 * defines it: the listing is `{ items: Thread[] }` (unpaged, no
 * `nextCursor`) and a message page is `{ items: Message[], nextCursor? }`.
 * One shape for every collection, so a reader that learns one has learned
 * them all. (The "title-prefix lookup" defect an earlier version of this
 * comment preserved was vendor history; the first-party listing reads our own
 * `correspondence_threads` by request id.)
 */

import { DEFAULT_PAGE_LIMIT, MAX_PAGE_LIMIT } from './provider.types';

import type {
  MentionableResponse,
  Message,
  SpaceId,
  Thread,
  ThreadFile,
  ThreadFileListing,
} from '../correspondence.types';
import type { OutgoingMessageFile, UploadOptions } from './provider.types';
import type { IMessagingProvider, Page, PageRequest } from './provider.types';

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

/**
 * The one thing this provider needs from the outside world.
 *
 * Deliberately narrower than `src/services/api.ts`: everything about auth
 * headers, base URLs, timeouts and error shaping already lives there and is not
 * this adapter's business. Errors propagate unchanged, because our API has
 * already made them safe to show.
 */
export interface CorrespondenceTransport {
  request<T>(method: HttpMethod, path: string, body?: unknown): Promise<T>;
}

/**
 * How a transport is obtained, once, when the provider is built.
 *
 * ⚠️ IT TAKES NO IDENTITY, AND THAT IS THE SECURITY PROPERTY. Who the caller
 * is, is decided server-side from the Okta session and never from anything
 * the client sends. Until Story 2.3 the factory was handed a
 * `MessagingIdentity` "so a test transport can model which session is
 * calling"; both factories ignored it, the contract harness selected the
 * session through its own `mockUser`, and a factory that had started
 * serialising it onto the wire would have handed the client authority it
 * must not have. There is no `connect` route and there must not be one.
 */
export type CorrespondenceTransportFactory = () => CorrespondenceTransport;

/** Versioned mount. `src/services/api.ts` already ends its base URL in `/api`. */
const ROOT = '/v1';

const defaultTransportFactory: CorrespondenceTransportFactory = () => ({
  request: async <T>(method: HttpMethod, path: string, body?: unknown) => {
    // Required lazily: `src/services/api.ts` reaches for the auth service and
    // the device-aware API config, and neither should be touched merely because
    // this module was imported.
    // Typed STRUCTURALLY rather than as `typeof import(...)`, so a type-check
    // that includes this file (the server-project contract suite, Story 1.3
    // D3) does not pull the whole client API module and its `@env` alias in.
    const { api } = require('../../../services/api') as {
      api: {
        get<R>(path: string): Promise<R>;
        post<R>(path: string, body?: unknown): Promise<R>;
        patch<R>(path: string, body?: unknown): Promise<R>;
        delete<R>(path: string): Promise<R>;
      };
    };
    switch (method) {
      case 'POST':
        return api.post<T>(path, body ?? {});
      case 'PATCH':
        return api.patch<T>(path, body ?? {});
      case 'DELETE':
        return api.delete<T>(path);
      case 'GET':
      default:
        return api.get<T>(path);
    }
  },
});

/**
 * Refuse a limit the backend would answer nonsensically, and clamp one it would
 * quietly shrink.
 *
 * ⚠️ THIS IS ENFORCED CLIENT-SIDE AS WELL AS SERVER-SIDE, ON PURPOSE. A
 * non-positive or fractional limit must be refused BEFORE a request is issued —
 * a round-trip to be told "no" is a round-trip a scroll gesture can repeat, and
 * the contract promises no request is issued at all.
 */
export const boundedLimit = (limit?: number): number => {
  if (limit === undefined) return DEFAULT_PAGE_LIMIT;
  if (!Number.isInteger(limit) || limit <= 0) {
    throw new Error(
      `ApiProvider: page limit must be a positive integer; received ${String(
        limit,
      )}.`,
    );
  }
  return Math.min(limit, MAX_PAGE_LIMIT);
};

const encode = (value: string): string => encodeURIComponent(value);

/**
 * The two wire shapes a write answers with, checked structurally before they
 * are rendered. A vendor-era envelope (`{ message }`, `{ thread }`) or an
 * empty reply fails here rather than as `undefined` on a screen.
 */
const isMessage = (value: unknown): value is Message =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Message).id === 'string' &&
  typeof (value as Message).threadId === 'string' &&
  typeof (value as Message).authorId === 'string' &&
  typeof (value as Message).body === 'string';

const isThread = (value: unknown): value is Thread =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Thread).id === 'string' &&
  typeof (value as Thread).title === 'string' &&
  ((value as Thread).space === 'internal' ||
    (value as Thread).space === 'shared');

/**
 * The listing's shapes, checked structurally for the same reason the two
 * above are (#1069 Story 7.4, D13): a vendor-era envelope or an empty reply
 * must fail at the boundary, not as `undefined` on a screen.
 *
 * ⚠️ `kind` IS COMPARED, NOT LOOKED UP. `correspondence.md` records why a
 * lookup table keyed by a caller-controlled string is a `Map` and never an
 * object literal here: a value of `constructor` resolved on `Object.prototype`
 * and 500'd a whole endpoint. Two comparisons need no table at all.
 *
 * ⚠️ EVERY FIELD `ThreadFile` DECLARES REQUIRED IS CHECKED, and the reason is
 * `mimeType` (code review, 2026-09-18). It was not, and `extensionLabel`
 * (`attachmentPieces.tsx`) calls `file.mimeType.lastIndexOf('/')` — so a reply
 * item without one threw a TypeError inside the panel's render tree and took
 * the whole conversation down with it, which is strictly worse than the blank
 * this guard was written to prevent. `thumbnailUri` is optional but type-
 * checked when present, because `partitionForDisplay` treats it as truthy and
 * hands it to `<Image source={{ uri }}>`.
 */
const isThreadFile = (value: unknown): value is ThreadFile =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as ThreadFile).id === 'string' &&
  typeof (value as ThreadFile).messageId === 'string' &&
  typeof (value as ThreadFile).name === 'string' &&
  ((value as ThreadFile).kind === 'image' ||
    (value as ThreadFile).kind === 'document') &&
  typeof (value as ThreadFile).mimeType === 'string' &&
  typeof (value as ThreadFile).sizeBytes === 'number' &&
  typeof (value as ThreadFile).sentAt === 'string' &&
  typeof (value as ThreadFile).authorId === 'string' &&
  typeof (value as ThreadFile).authorName === 'string' &&
  ((value as ThreadFile).thumbnailUri === undefined ||
    typeof (value as ThreadFile).thumbnailUri === 'string');

const isThreadFileListing = (value: unknown): value is ThreadFileListing =>
  typeof value === 'object' &&
  value !== null &&
  Array.isArray((value as ThreadFileListing).items) &&
  (value as ThreadFileListing).items.every(isThreadFile);

/**
 * One JSON body with files over the progress transport (#1530).
 * `postMessageWithFiles` keeps its own copy of this wiring because it hands
 * an abort handle back SYNCHRONOUSLY, before the headers resolve; the two
 * calls that gained files in #1530 (`createThread`, `editMessage`) have no
 * pending row today, so their callers may leave `options` out. But the
 * transport's progress events and abort handle are not discarded here (#1530
 * review, C1): a caller that passes `options` gets them, the same way the
 * post does, so a future pending row for a create or an edit is a wiring
 * change in the hook and not in the transport.
 *
 * ⚠️ THE URL AND HEADERS COME FROM `api.ts`, as `postMessageWithFiles`'s
 * comment says; the lazy `require`s are the same ones, for the same reason.
 */
const uploadJson = async <T>(
  method: 'POST' | 'PATCH',
  path: string,
  payload: unknown,
  options?: UploadOptions,
): Promise<T> => {
  const { buildApiHeaders, getResolvedApiBaseUrl } =
    require('../../../services/api') as {
      buildApiHeaders: (
        extra?: Record<string, string>,
      ) => Promise<Record<string, string>>;
      getResolvedApiBaseUrl: () => string;
    };
  const { postJsonWithProgress } =
    require('../../../services/correspondence/postWithProgress') as {
      postJsonWithProgress: typeof import('../../../services/correspondence/postWithProgress').postJsonWithProgress;
    };
  const headers = await buildApiHeaders();
  const upload = postJsonWithProgress<T>({
    method,
    url: `${getResolvedApiBaseUrl()}${path}`,
    headers,
    payload,
    onProgress: options?.onProgress,
  });
  options?.onUploadStarted?.(upload.abort);
  return upload.promise;
};

export class ApiProvider implements IMessagingProvider {
  readonly name = 'api' as const;

  /**
   * Built in the constructor (Story 2.3, D8): there is no session to open
   * and none to close, so the provider is usable from the moment it exists.
   *
   * Declared as a field and assigned in the body, NOT as a TypeScript
   * parameter property. `constructor(private readonly transport = ...)` is
   * valid TypeScript and Jest's transform accepts it, but the webpack/Babel
   * pipeline that builds the web bundle emitted `private readonly` straight
   * into the JavaScript and the bundle failed to parse: "The keyword
   * 'private' is reserved". No other file under `src/` uses a parameter
   * property, so the web build had never had to transpile one.
   */
  private readonly transport: CorrespondenceTransport;

  constructor(
    makeTransport: CorrespondenceTransportFactory = defaultTransportFactory,
  ) {
    this.transport = makeTransport();
  }

  /**
   * The request's conversations in one audience, newest activity first, as
   * `{ items: Thread[] }` (Story 1.2, AC 4). An audience the caller may not
   * see comes back as an empty page, indistinguishable from an empty one.
   */
  async listThreads(requestId: string, space: SpaceId): Promise<Thread[]> {
    const page = await this.transport.request<Page<Thread> | undefined>(
      'GET',
      `${ROOT}/requests/${encode(
        requestId,
      )}/correspondence/threads?space=${encode(space)}`,
    );
    // A page that is not a page is an ERROR, not an empty list: "No
    // discussions yet." is a false statement about a real team member's
    // request (AC 8 rationale), and the vendor `{ threads }` shape this story
    // removed must fail loudly if it ever comes back (review, group 3).
    if (!page || !Array.isArray(page.items)) {
      throw new Error('ApiProvider.listThreads: malformed page');
    }
    return page.items;
  }

  /**
   * Start a conversation with its first message (Story 1.3, C10): the server
   * answers `201 { thread, message }`. The thread is returned; the message is
   * on the server and arrives with the ordinary page load when the panel
   * opens the new conversation.
   */
  async createThread(
    requestId: string,
    space: SpaceId,
    title: string,
    body: string,
    files: OutgoingMessageFile[] = [],
    options?: UploadOptions,
  ): Promise<Thread> {
    const path = `${ROOT}/requests/${encode(requestId)}/correspondence/threads`;
    // #1530: a first message carrying files is the same base64-in-JSON shape
    // as a files post, so it rides the progress transport (ceilings, stall
    // detector) rather than the `fetch` seam; a text-only create is unchanged.
    const created =
      files.length > 0
        ? await uploadJson<{ thread: Thread; message: Message } | undefined>(
            'POST',
            path,
            { space, title, body, files },
            options,
          )
        : await this.transport.request<
            { thread: Thread; message: Message } | undefined
          >('POST', path, {
            space,
            title,
            body,
          });
    if (!created || !isThread(created.thread) || !isMessage(created.message)) {
      throw new Error('ApiProvider.createThread: malformed reply');
    }
    return created.thread;
  }

  /**
   * Newest first, walking BACKWARDS through history (FR6).
   *
   * ⚠️ THE CURSOR IS OPAQUE IN BOTH DIRECTIONS. It is handed back exactly as it
   * arrived — never decoded, never re-minted, never compared. Its scope is
   * validated by our API, which is the only side that knows what it means, and
   * a bad one comes back as a refusal rather than as a silent restart.
   */
  async listMessages(
    threadId: string,
    page: PageRequest = {},
  ): Promise<Page<Message>> {
    // Before the transport is touched: an invalid limit must cost no request.
    const limit = boundedLimit(page.limit);
    const query = `limit=${limit}${
      page.cursor ? `&cursor=${encode(page.cursor)}` : ''
    }`;
    const reply = await this.transport.request<Page<Message> | undefined>(
      'GET',
      `${ROOT}/correspondence/threads/${encode(threadId)}/messages?${query}`,
    );
    // The one read that did not check its reply (review): a malformed page
    // surfaced as a `TypeError` from `[...page.items].reverse()` in the hook.
    // Same rule as `listThreads`: not a page is an error, never an empty list.
    if (
      !reply ||
      !Array.isArray(reply.items) ||
      !reply.items.every(isMessage) ||
      (reply.nextCursor !== undefined && typeof reply.nextCursor !== 'string')
    ) {
      throw new Error('ApiProvider.listMessages: malformed page');
    }
    return reply;
  }

  /**
   * `GET …/correspondence/mentionable?space=` (#1599 Story 1.1 / 1.4): the
   * @ suggester's list. A reply that is not `{ space, people[] }` is an error,
   * never an empty list, for the reason `listThreads` gives.
   */
  async listMentionable(
    requestId: string,
    space: SpaceId,
  ): Promise<MentionableResponse> {
    const reply = await this.transport.request<MentionableResponse | undefined>(
      'GET',
      `${ROOT}/requests/${encode(
        requestId,
      )}/correspondence/mentionable?space=${encode(space)}`,
    );
    if (
      !reply ||
      (reply.space !== 'internal' && reply.space !== 'shared') ||
      !Array.isArray(reply.people) ||
      // Each row is sorted and filtered by name on every keystroke, so a row
      // without string fields would throw inside the panel's render.
      !reply.people.every(
        (person: unknown) =>
          typeof person === 'object' &&
          person !== null &&
          typeof (person as { userId?: unknown }).userId === 'string' &&
          typeof (person as { name?: unknown }).name === 'string' &&
          typeof (person as { email?: unknown }).email === 'string',
      )
    ) {
      throw new Error('ApiProvider.listMentionable: malformed reply');
    }
    return reply;
  }

  /** `201 Message` (Story 1.3, C10): the saved message, name from the join. */
  async postMessage(threadId: string, body: string): Promise<Message> {
    const message = await this.transport.request<Message | undefined>(
      'POST',
      `${ROOT}/correspondence/threads/${encode(threadId)}/messages`,
      { body },
    );
    if (!isMessage(message)) {
      throw new Error('ApiProvider.postMessage: malformed reply');
    }
    return message;
  }

  /**
   * Post a message and its files in ONE request (#1069 Story 4.1, AC 5).
   *
   * ⚠️ NOT THROUGH `this.transport`. That seam is `fetch`-shaped: it cannot
   * report upload progress on native and cannot be aborted mid-body (C2), and
   * AC 9 and AC 11 need both. `postJsonWithProgress` drives a real
   * `XMLHttpRequest` on every platform instead.
   *
   * ⚠️ THE URL AND HEADERS COME FROM `api.ts`, not from a second copy here.
   * `getResolvedApiBaseUrl` and `buildApiHeaders` are exported for exactly this
   * call so it authenticates identically to every other one; re-deriving either
   * is how one path quietly talks to the wrong host or stops sending a token.
   *
   * Headers are resolved lazily INSIDE the returned promise, because the JWT
   * lookup is async and the caller needs the `abort` handle synchronously: a
   * user who cancels in the first moments must not find the control inert.
   */
  postMessageWithFiles(args: {
    threadId: string;
    body: string;
    files: OutgoingMessageFile[];
    onProgress?: (percent: number | null) => void;
  }): { promise: Promise<Message>; abort: () => void } {
    const { threadId, body, files, onProgress } = args;
    let abortUpload: (() => void) | null = null;
    let abortedBeforeStart = false;

    const promise = (async (): Promise<Message> => {
      const { buildApiHeaders, getResolvedApiBaseUrl } =
        require('../../../services/api') as {
          buildApiHeaders: (
            extra?: Record<string, string>,
          ) => Promise<Record<string, string>>;
          getResolvedApiBaseUrl: () => string;
        };
      const { postJsonWithProgress } =
        require('../../../services/correspondence/postWithProgress') as {
          postJsonWithProgress: typeof import('../../../services/correspondence/postWithProgress').postJsonWithProgress;
        };

      const headers = await buildApiHeaders();
      const url = `${getResolvedApiBaseUrl()}${ROOT}/correspondence/threads/${encode(
        threadId,
      )}/messages`;

      const upload = postJsonWithProgress<Message | undefined>({
        url,
        headers,
        payload: { body, files },
        onProgress,
      });
      abortUpload = upload.abort;
      // Cancelled while the token was still being fetched: honour it rather
      // than letting the request start and finish unwatched.
      if (abortedBeforeStart) upload.abort();

      const message = await upload.promise;
      if (!isMessage(message)) {
        throw new Error('ApiProvider.postMessageWithFiles: malformed reply');
      }
      return message;
    })();

    return {
      promise,
      abort: () => {
        if (abortUpload) abortUpload();
        else abortedBeforeStart = true;
      },
    };
  }

  /**
   * ⚠️ THIS CALL CONTACTS NO MESSAGING BACKEND, which is the one thing worth
   * knowing about it. Our API authorizes it against the request-to-thread index
   * and writes a single row; it is deliberately as cheap as a thread click, and
   * the panel makes one per open.
   *
   * No body: the thread is in the path and the user is the session.
   */
  async markThreadRead(threadId: string): Promise<void> {
    await this.transport.request<void>(
      'POST',
      `${ROOT}/correspondence/threads/${encode(threadId)}/read`,
    );
  }

  /** `200 Message` carrying `editedAt` (Story 1.3, C10). */
  async editMessage(
    threadId: string,
    messageId: string,
    body: string,
    files: OutgoingMessageFile[] = [],
    options?: UploadOptions,
  ): Promise<Message> {
    const path = `${ROOT}/correspondence/threads/${encode(
      threadId,
    )}/messages/${encode(messageId)}`;
    // #1530: an edit that adds files is a PATCH over the progress transport,
    // for the reason `createThread` gives; a text-only edit is unchanged.
    const message =
      files.length > 0
        ? await uploadJson<Message | undefined>(
            'PATCH',
            path,
            { body, files },
            options,
          )
        : await this.transport.request<Message | undefined>('PATCH', path, {
            body,
          });
    if (!isMessage(message)) {
      throw new Error('ApiProvider.editMessage: malformed reply');
    }
    return message;
  }

  /** `200 Thread` in the listing shape (Story 7.3, D14). */
  async renameThread(threadId: string, title: string): Promise<Thread> {
    const thread = await this.transport.request<Thread | undefined>(
      'PATCH',
      `${ROOT}/correspondence/threads/${encode(threadId)}`,
      { title },
    );
    if (!isThread(thread)) {
      throw new Error('ApiProvider.renameThread: malformed reply');
    }
    return thread;
  }

  /**
   * The conversation's files, one GET with no body (#1069 Story 7.4).
   *
   * ⚠️ NO `limit` AND NO `cursor` IN THE PATH. The ceiling is the server's and
   * the shape carries no cursor; a client that could ask for a page would be a
   * client that thinks this is one.
   */
  async listThreadFiles(threadId: string): Promise<ThreadFileListing> {
    const listing = await this.transport.request<ThreadFileListing | undefined>(
      'GET',
      `${ROOT}/correspondence/threads/${encode(threadId)}/files`,
    );
    if (!isThreadFileListing(listing)) {
      throw new Error('ApiProvider.listThreadFiles: malformed reply');
    }
    return listing;
  }

  async deleteMessage(threadId: string, messageId: string): Promise<void> {
    await this.transport.request<void>(
      'DELETE',
      `${ROOT}/correspondence/threads/${encode(threadId)}/messages/${encode(
        messageId,
      )}`,
    );
  }
}
